import { SimNaoEnum, simNaoEnumToBoolean } from 'components/form'
import { validateEnderecoCompleto } from 'components/form/field/endereco/validator-endereco'
import { LotacaoAndEstagioSelectFieldModel } from 'components/form/field/select/LotacaoAndEstagioSelectField/LotacaoAndEstagioSelectField'
import { LotacaoResponsavelFormModel } from 'components/lotacao-responsavel/model-lotacaoResponsavel'
import { NacionalidadeEnum, RacaCorDbEnum } from 'graphql/types.generated'
import { isUndefinedOrNull } from 'util/checks'
import {
  afterEqualTo,
  alfaNumerico,
  beforeEqualToday,
  cns,
  composeRules,
  cpf,
  createValidator,
  dataNasc,
  email,
  empty,
  ErrorObject,
  length,
  maxLength,
  microarea,
  nome,
  required,
  simpleName,
  telefone,
} from 'util/validation'

import { ContatosFormModel } from '../types/model-cidadao'
import {
  CidadaoFciFormModel,
  CondicoesSaudeAutorreferidasFormModel,
  DadosPessoaisFormModel,
  InformacoesSocioEconomicasFormModel,
  NacionalidadeFormModel,
  ResponsabilidadeAcompanhamentoFormModel,
  SituacaoRuaFormModel,
} from './model-cidadaoFci'

export const dadosPessoaisCidadaoFciValidator = () =>
  createValidator<DadosPessoaisFormModel>(
    {
      cpf: cpf,
      cns: cns,
      sexo: [required],
      racaCor: [required],
      nisPisPasep: [length(11)],
      dataNascimento: [required, dataNasc],
      nome: [required, nome(3, 70)],
      nomeSocial: [simpleName, maxLength(70)],
      desejaInformarIdentidadeGenero: [required],
      desejaInformarOrientacaoSexual: [required],
    },
    (values: DadosPessoaisFormModel, errors: ErrorObject<DadosPessoaisFormModel>) => {
      if (values.desconheceNomeMae !== true) {
        errors.nomeMae = composeRules([required, nome(3, 70)])(values.nomeMae)
      } else {
        errors.nomeMae = empty(values.nomeMae)
      }

      if (values.desconheceNomePai !== true) {
        errors.nomePai = composeRules([required, nome(3, 70)])(values.nomePai)
      } else {
        errors.nomePai = empty(values.nomePai)
      }

      if (values.racaCor?.value !== RacaCorDbEnum.INDIGENA && values.etnia) {
        errors.etnia = empty(values.etnia)
      }

      if (values.racaCor?.value === RacaCorDbEnum.INDIGENA) {
        errors.etnia = required(values.etnia)
      }

      if (values?.faleceu) {
        errors.dataObito = dataObitoComposedRule(
          values.dataObito,
          values.dataNascimento,
          values.nacionalidadeCidadao.dataEntradaPais,
          values.nacionalidadeCidadao.dataNaturalizacao
        )
      } else {
        errors.dataObito = empty(values.dataObito)
        errors.numeroDeclaracaoObito = empty(values.numeroDeclaracaoObito)
      }

      errors.nacionalidadeCidadao = nacionalidadeFciValidator(values.dataNascimento)(values.nacionalidadeCidadao)

      if (simNaoEnumToBoolean[values.desejaInformarIdentidadeGenero]) {
        errors.identidadeGenero = required(values.identidadeGenero)
      } else {
        errors.identidadeGenero = empty(values.identidadeGenero)
      }

      if (simNaoEnumToBoolean[values.desejaInformarOrientacaoSexual]) {
        errors.orientacaoSexual = required(values.orientacaoSexual)
      } else {
        errors.orientacaoSexual = empty(values.orientacaoSexual)
      }

      return errors
    }
  )

export const nacionalidadeFciValidator = (dataNascimento: string) =>
  createValidator<NacionalidadeFormModel>(
    {
      nacionalidade: [required],
    },
    (values: NacionalidadeFormModel, errors: ErrorObject<NacionalidadeFormModel>) => {
      if (values.nacionalidade === NacionalidadeEnum.BRASILEIRA) {
        errors.municipioNascimento = required(values.municipioNascimento)
      }

      if (values.nacionalidade === NacionalidadeEnum.NATURALIZADA) {
        errors.portariaNaturalizacao = composeRules([required, maxLength(16), alfaNumerico])(
          values.portariaNaturalizacao
        )
        errors.dataNaturalizacao = composeRules([
          required,
          beforeEqualToday,
          checkIsAfterEqualNascimento(values.dataNaturalizacao, dataNascimento),
        ])(values.dataNaturalizacao)
      }

      if (values.nacionalidade === NacionalidadeEnum.ESTRANGEIRA) {
        errors.paisNascimento = required(values.paisNascimento)
        errors.dataEntradaPais = composeRules([
          required,
          beforeEqualToday,
          checkIsAfterEqualNascimento(values.dataEntradaPais, dataNascimento),
        ])(values.dataEntradaPais)
      }

      return errors
    }
  )

export const informacoesSocioEconomicasValidator = () =>
  createValidator<InformacoesSocioEconomicasFormModel>(
    {},
    (values: InformacoesSocioEconomicasFormModel, errors: ErrorObject<InformacoesSocioEconomicasFormModel>) => {
      if (
        !isUndefinedOrNull(values?.alimentosAcabaramAntesTerDinheiroComprarMais) ||
        !isUndefinedOrNull(values?.comeuAlgunsAlimentosDinheiroAcabou)
      ) {
        errors.alimentosAcabaramAntesTerDinheiroComprarMais = required(
          values.alimentosAcabaramAntesTerDinheiroComprarMais
        )
        errors.comeuAlgunsAlimentosDinheiroAcabou = required(values.comeuAlgunsAlimentosDinheiroAcabou)
      }

      return errors
    }
  )

export const contatosValidator = (nacionalidade: NacionalidadeEnum) =>
  createValidator<ContatosFormModel>(
    {
      telefoneCelular: [telefone],
      telefoneResidencial: [telefone],
      telefoneContato: [telefone],
      email: [email],
    },
    (values: ContatosFormModel, errors: ErrorObject<ContatosFormModel>) => {
      if (!errors.telefoneCelular && nacionalidade !== NacionalidadeEnum.ESTRANGEIRA) {
        errors.telefoneCelular = required(values?.telefoneCelular)
      }

      return errors
    }
  )

export const condicoesSaudeAutorreferidasValidator = (dataNascimento: string) =>
  createValidator<CondicoesSaudeAutorreferidasFormModel>(
    {
      maternidadeReferencia: [maxLength(100)],
      causaInternacaoUltimos12Meses: [maxLength(100)],
      plantasMedicinais: [maxLength(100)],
      condicaoSaude1: [maxLength(100)],
      condicaoSaude2: [maxLength(100)],
      condicaoSaude3: [maxLength(100)],
    },
    (values: CondicoesSaudeAutorreferidasFormModel, errors: ErrorObject<CondicoesSaudeAutorreferidasFormModel>) => {
      if (values?.stDoencaCardiaca === SimNaoEnum.SIM) {
        if (!values.doencaCardiaca || values.doencaCardiaca.length === 0) {
          errors.doencaCardiaca = required(values.doencaCardiaca)
        }
      }

      if (values?.stDoencaRespiratoria === SimNaoEnum.SIM) {
        if (!values.doencasRespiratorias || values.doencasRespiratorias.length === 0) {
          errors.doencasRespiratorias = required(values.doencasRespiratorias)
        }
      }

      if (values?.stProblemaRins === SimNaoEnum.SIM) {
        if (!values.doencaRins || values.doencaRins.length === 0) {
          errors.doencaRins = required(values.doencaRins)
        }
      }

      const hoje = new Date()
      const nascimentoDate = dataNascimento ? new Date(dataNascimento) : null

      if (nascimentoDate) {
        const limiteInferior = new Date(hoje.getFullYear() - 9, hoje.getMonth(), hoje.getDate())
        const limiteSuperior = new Date(hoje.getFullYear() - 60, hoje.getMonth(), hoje.getDate())

        const idadeForaFaixa = nascimentoDate > limiteInferior || nascimentoDate < limiteSuperior

        if (idadeForaFaixa) {
          if (values?.stGestante === SimNaoEnum.SIM) errors.stGestante = 'Idade fora da faixa etária permitida.'
          if (values?.maternidadeReferencia) errors.maternidadeReferencia = empty(values.maternidadeReferencia)
        }
      }

      return errors
    }
  )

export const cidadaoFciValidator = () =>
  createValidator<CidadaoFciFormModel>(
    {
      dadosPessoais: [dadosPessoaisCidadaoFciValidator()],
      endereco: validateEnderecoCompleto,
      informacoesSocioEconomicas: [informacoesSocioEconomicasValidator()],
      situacaoRua: [situacaoDeRuaValidator()],
      statusSituacaoRua: [required],
      responsabilidadeAcompanhamento: [validateResponsabilidadeAcompanhamento()],
    },
    (values: CidadaoFciFormModel, errors: ErrorObject<CidadaoFciFormModel>) => {
      errors.condicoesSaudeAutorreferidas = condicoesSaudeAutorreferidasValidator(
        values?.dadosPessoais?.dataNascimento
      )(values?.condicoesSaudeAutorreferidas)

      errors.contatos = contatosValidator(values?.dadosPessoais?.nacionalidadeCidadao?.nacionalidade)(values?.contatos)
      return errors
    }
  )

const checkIsAfterEqualNascimento = (data: string, dataNascimento: string) => () =>
  afterEqualTo(data, dataNascimento, 'data nascimento')

const dataObitoComposedRule = (
  dataObito: string,
  dataNascimento: string,
  dataEntradaPais?: string,
  dataNaturalizacao?: string
) => {
  return composeRules([
    required,
    beforeEqualToday,
    checkIsAfterEqualNascimento(dataObito, dataNascimento),
    () => afterEqualTo(dataObito, dataEntradaPais, 'data de entrada no país'),
    () => afterEqualTo(dataObito, dataNaturalizacao, 'data de naturalização'),
  ])(dataObito)
}

export const situacaoDeRuaValidator = () =>
  createValidator<SituacaoRuaFormModel>(
    {
      grauParentescoVisitaFamiliar: [maxLength(100)],
      instituicaoAcompanhamento: [maxLength(100)],
    },
    (values: SituacaoRuaFormModel, errors: ErrorObject<SituacaoRuaFormModel>) => {
      if (values?.possuiAcessoHigienePessoal === SimNaoEnum.SIM) {
        errors.higienePessoal = required(values.higienePessoal)
      }

      return errors
    }
  )

export const validateResponsabilidadeAcompanhamento = () => {
  return createValidator<ResponsabilidadeAcompanhamentoFormModel>(
    {
      microarea: [microarea],
    },
    (values: ResponsabilidadeAcompanhamentoFormModel, errors: ErrorObject<ResponsabilidadeAcompanhamentoFormModel>) => {
      if (values?.responsavel) {
        errors.responsavel = validateResponsavel()(values?.responsavel)
      }

      if (!errors?.microarea && values?.statusForaArea !== true) {
        errors.microarea = required(values?.microarea)
      }

      return errors
    }
  )
}

export const validateResponsavel = () => {
  return createValidator<LotacaoResponsavelFormModel>(
    {
      lotacao: [required],
    },
    (values: LotacaoResponsavelFormModel, errors: ErrorObject<LotacaoResponsavelFormModel>) => {
      if (!!values?.lotacao) {
        errors.lotacao = validateLotacao()(values?.lotacao)
      }
      return errors
    }
  )
}

export const validateLotacao = () => {
  return createValidator<LotacaoAndEstagioSelectFieldModel>(
    {
      profissional: [required],
      unidadeSaude: [required],
      cbo: [required],
    },
    (values: LotacaoAndEstagioSelectFieldModel, errors: ErrorObject<LotacaoAndEstagioSelectFieldModel>) => {
      if (values?.cbo && !values.cbo?.actions.cadastroIndividual.enabled) {
        errors.cbo = 'O responsável informado possui um CBO inválido para um cidadão.'
      }
      return errors
    }
  )
}
